import clsx from 'clsx';
import React, {
  createElement,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  FunctionComponent,
  HTMLProps,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
  ReactHTML,
  useMemo
} from 'react';
import { Keys, useKeypress } from '../../../hooks/use-keypress';
import { Typofallback } from '../../typofallback';

import classes from './list-item.module.scss';

export type ListItemProps<P extends object = HTMLProps<HTMLLIElement>, T = object>
  = PropsWithChildren & {
    /**
     * Prefix element
     */
    prefix?: ReactElement;

    /**
   * Suffix element
   */
    suffix?: ReactElement;

    /**
   * Custom classname
   */
    className?: string;

    /**
   * Custom click handler
   */
    onClick?: MouseEventHandler<HTMLButtonElement>;

    /**
   * Render as clickable
   */
    clickable?: boolean;

    /**
   * Render as active
   */
    active?: boolean;

    /**
   * Render as selected
   */
    selected?: boolean;

    /**
   * Disabled state
   */
    disabled?: boolean;

    /**
   * Render as subheader
   */
    subheader?: boolean;

    fixedSuffixSize?: boolean;

    style?: CSSProperties;

    classes?: {
      wrapper?: string;
      content?: string;
    };

    /**
   * `compact` reduces padding
   */
    variant?: 'compact';

    /**
     * Custom button props if item is clickable
     */
    buttonProps?: Omit<Partial<HTMLProps<HTMLButtonElement>>, 'type'>;
  } & Partial<Omit<HTMLLIElement, 'prefix' | 'children' | 'style'>> & ({
    as: 'a';
    href?: string;
    target?: string;
    rel?: string;
  } | {
    as?: FunctionComponent<P> | keyof ReactHTML | T;
  });

export const ListItem = forwardRef(<T extends object, P>({
  children,
  onClick,
  clickable,
  prefix,
  suffix,
  active,
  selected,
  className,
  disabled,
  subheader,
  as,
  classes: customClasses,
  fixedSuffixSize = true,
  variant,
  buttonProps,
  ...props
}: ListItemProps<T, P>, ref: ForwardedRef<HTMLLIElement>) => {
  // eslint-disable-next-line
  // @ts-ignore
  const keyDownListener = useKeypress(Keys.ENTER, () => onClick?.());
  const content = useMemo(() => {
    return <span className={clsx(classes.contentWrapper, customClasses?.wrapper)}>
      {prefix && <span className={classes.prefix}>{prefix}</span>}
      <span className={clsx(classes.content, customClasses?.content)}>
        <Typofallback as={'span'}>
          {children}
        </Typofallback>
      </span>
      {suffix && <span className={classes.suffix}>{suffix}</span>}
    </span>;
  }, [customClasses, children, prefix, suffix]);

  const wrapper = useMemo(() => {
    if (onClick) {
      return <button {...buttonProps} onClick={onClick} className={classes.button}>
        {content}
      </button>;
    }

    return <>
      {content}
    </>;
  }, [content, onClick]);

  return createElement(
    (as ?? 'div') as keyof ReactHTML,
    {
      ...props,
      ref,
      disabled,
      role: 'listitem',
      onKeyDown: keyDownListener,
      className: clsx(classes.root, className, 'list-item-component', {
        [classes.withPrefix]: !!prefix,
        [classes.withSuffix]: !!suffix,
        [classes.clickable]: clickable || !!onClick,
        [classes.active]: active,
        [classes.selected]: selected,
        [classes.disabled]: disabled,
        [classes.subheader]: subheader,
        [classes.isSuffixFixedSize]: fixedSuffixSize,
        [classes.compact]: variant === 'compact'
      })
    },
    wrapper
  );
});